import { createAsyncAction, errorResult, successResult } from "pullstate";
import { GlobalStore } from "./GlobalStore";
import { TokenList } from "./getAllTokenLists";
import * as Sentry from "@sentry/browser";
import { Amount } from "../../types/amount";
import { Chain } from "@rainbow-me/rainbowkit";
import { fetchBalancesFast } from "./getBalancesFast";
import { cloneDeep } from "lodash-es";
import { SENTRY_CONTEXTS } from "../../config/consts";
import { transformPricesToBalances } from "../../utils/tokenListUtils";
import { getIsUserConnected } from "utils/userUtils";
import { Hash } from "viem";

export type Balances = Array<Balance>;

export interface Balance {
  tokenAddress: Hash;
  balance: Amount;
}

export const getBalances = createAsyncAction(
  async ({
    address,
    tokenList,
    chain,
  }: {
    address: `0x${string}`;
    tokenList: TokenList;
    chain: Chain;
  }) => {
    try {
      Sentry.configureScope(scope => {
        scope.setTransactionName(SENTRY_CONTEXTS.BALANCES);
      });
      const result = {
        chain: chain,
        balances: await fetchBalancesFast(address, tokenList, chain),
      };
      if (result) {
        return successResult(result);
      }
    } catch (e) {
      Sentry.captureException(`Could not get balances`, { extra: { event: e } });
      console.error(`Could not get balances`, e);
      return errorResult([], `Could not get balances`, e);
    }
    Sentry.captureException(`Could not get balances`);
    console.error(`Could not get balances`);
    return errorResult([], `Couldn't get balances`);
  },
  {
    postActionHook: ({ result }) => {
      const resultClone = cloneDeep(result);
      if (!resultClone.error && getIsUserConnected()) {
        GlobalStore.update(s => {
          if (s.address) {
            if (resultClone.payload.chain.id !== s.network.id) {
              console.log(
                `Got balances for ${resultClone.payload.chain.id} but was expecting ${s.network.id}`
              );
              return;
            }
            s.balances = resultClone.payload.balances;
          }
        });
      }
    },
  }
);

export const clearBalances = () => {
  GlobalStore.update(s => {
    const tokenList = s.tokenLists?.[s.network.id];
    if (tokenList != null) {
      s.balances = transformPricesToBalances(tokenList);
    }
  });
};
